/**
 * The training contract (I2Q sales training).
 *
 * Frozen in the first training commit. The content author writes `content/**` against
 * these types only; the platform (player, practice, integration) reads them. Change it
 * only by agreement between both.
 */

import type { FigureName } from "@/components/figures";
import type { HelpTopicId } from "@/features/help";

/** Must equal `MASTERY_PERCENT` in `backend/src/rfq_foundry/training.py` (a test reads both). */
export const MASTERY_PERCENT = 80;
export const COURSE_ID = "i2q-sales" as const;

export type ModuleId =
  | "welcome"
  | "queue"
  | "customer"
  | "lines"
  | "prices"
  | "reply"
  | "status"
  | "extras"
  | "quiz"
  | "closing";

/** Stored as a module row of `i2q-sales`. */
export const QUICK_START_MODULE = "quick-start" as const;
export const FINAL_QUIZ_MODULE: ModuleId = "quiz";

export type QuizTopic =
  | "shadow"
  | "queue"
  | "customer"
  | "lines"
  | "prices"
  | "reply"
  | "status"
  | "feedback";

export type SlideTone = "dark" | "light" | "grey";

/**
 * Plain text. Inline marks: **bold**, `Screen label` (rendered as a UI chip), {key:c}
 * (rendered as <Kbd>). Nothing else.
 */
export type Text = string;

export interface Course {
  id: typeof COURSE_ID;
  title: Text;
  subtitle: Text;
  contentVersion: number;
  modules: Module[];
}

/**
 * The static manager page (`/training/managers`, MA-05): what the team will see, in a
 * few short sections. No progress, no quiz, plain language, no codes.
 */
export interface ManagerBrief {
  title: Text;
  subtitle: Text;
  /** About how long the page takes to read. */
  minutes: number;
  sections: { id: string; title: Text; body: Block[] }[];
  /** The five things a sales person does with a case, for the team section of Operations. */
  teamSteps: Text[];
}

export interface Module {
  id: ModuleId;
  title: Text;
  menuTitle: Text;
  minutes: number;
  goals: Text[];
  kind: "lesson" | "quiz" | "closing";
  /** quiz: exactly 1 slide whose interaction is {kind:"quiz"} */
  slides: Slide[];
  /** "Open the full help" links on the last slide */
  helpTopics?: HelpTopicId[];
}

export interface Slide {
  /** unique in module, /^[a-z][a-z0-9-]{0,23}$/ (w1, q2 ...) */
  id: string;
  tone: SlideTone;
  title: Text;
  /** starts "When you need to" or an imperative */
  job: Text;
  /** click path, rendered "Sidebar > Queue > Older than 7 days" */
  path?: Text[];
  body?: Block[];
  interaction?: Interaction;
  /** "Check before you use it" line */
  check: Text;
  /** exact UI strings quoted; a test greps frontend/src for each (skipped when pending) */
  labels?: string[];
  /** shows the availability badge */
  pending?: boolean;
}

export type Block =
  | { kind: "text"; text: Text }
  | { kind: "list"; items: Text[] }
  | { kind: "steps"; steps: Text[] }
  | { kind: "note"; tone: "info" | "warning"; title: Text; text: Text }
  | { kind: "keys"; keys: { key: string; does: Text }[] }
  /** A figure of `components/figures` (round 3, 12 Oct 2026), with an optional caption. */
  | { kind: "figure"; figure: FigureName; caption?: Text };

// Practice stage: real components on synthetic data

export type PracticeCaseId =
  | "PRACTICE-0001"
  | "PRACTICE-0002"
  | "PRACTICE-0003"
  | "PRACTICE-0004"
  | "PRACTICE-0005";

/**
 * Hotspot targets on a practice stage, named after the three-step case page of the
 * minimal redesign (12 Oct 2026): the customer line, the draft rows and their Details,
 * the one action with its More menu, and the Details sheet with the checks.
 */
export type PracticeAnchor =
  | "shadow-badge"
  | "queue-badge"
  | "queue-backlog-chips"
  | "queue-tabs"
  | "queue-action"
  | "queue-lines"
  | "case-customer"
  | "case-lines"
  | "case-line-details"
  | "case-history"
  | "case-rules"
  | "case-reply"
  | "case-action"
  | "case-mail";

export type PracticeStage =
  | { view: "queue"; preset?: "default" | "all-dates" | "closed" | "back-for-you" }
  | {
      view: "case";
      caseId: PracticeCaseId;
      /** `action` shows the work column from the action down (the More menu lesson). */
      part?: "full" | "customer" | "lines" | "prices" | "reply" | "action" | "mail";
      role?: "sales" | "manager";
      after?: "customer-confirmed";
    };

export type PracticeTaskId =
  | "find-backlog"
  | "confirm-customer-with-evidence"
  | "reject-guess-pick-another"
  | "accept-matched-and-fix-quantity"
  | "copy-reply-and-fix-text"
  | "take-and-set-waiting"
  | "reopen-closed";

/** `required` gates Next on this slide. */
interface Base {
  id: string;
  required: boolean;
}

export interface Hotspots extends Base {
  kind: "hotspots";
  stage: PracticeStage;
  spots: { anchor: PracticeAnchor; label: Text; text: Text }[];
  minOpen: number;
}
export interface SpotTheIssue extends Base {
  kind: "spot";
  prompt: Text;
  stage?: PracticeStage;
  rows: { id: string; text: Text; issue: boolean; why: Text }[];
  mustFind: number;
}
export interface SortButtons extends Base {
  kind: "sort";
  prompt: Text;
  bins: [Text, Text] | [Text, Text, Text];
  cards: { id: string; text: Text; bin: 0 | 1 | 2; why: Text }[];
}
export interface RevealGate extends Base {
  kind: "reveal";
  cards: { id: string; front: Text; back: Text; module?: ModuleId }[];
  minOpen: number;
}
export interface BeforeAfter extends Base {
  kind: "before-after";
  tabs: { id: string; label: Text; stage?: PracticeStage; blocks?: Block[] }[];
}
export interface Scenario extends Base {
  kind: "scenario";
  start: string;
  stage?: PracticeStage;
  nodes: Record<
    string,
    {
      text: Text;
      choices: {
        id: string;
        text: Text;
        outcome: "good" | "ok" | "bad";
        fb: Text;
        next?: string;
      }[];
    }
  >;
}
export interface CopyTemplate extends Base {
  kind: "copy";
  templates: { id: string; title: Text; text: string }[];
}
/** Answer until right, not scored. */
export interface InlineCheck extends Base {
  kind: "check";
  question: QuizQuestion;
}
export interface PracticeTask extends Base {
  kind: "practice";
  stage: PracticeStage;
  task: PracticeTaskId;
  steps: Text[];
  success: Text;
  hints: Text[];
}
export interface QuizOption {
  id: string;
  text: Text;
  correct: boolean;
  fb: Text;
}
export interface QuizQuestion {
  id: string;
  topic: QuizTopic;
  stem: Text;
  stage?: PracticeStage;
  options: QuizOption[];
}
export interface Quiz extends Base {
  kind: "quiz";
  questions: QuizQuestion[];
  topicHints: Record<QuizTopic, { module: ModuleId; text: Text }>;
}

export type Interaction =
  | Hotspots
  | SpotTheIssue
  | SortButtons
  | RevealGate
  | BeforeAfter
  | Scenario
  | CopyTemplate
  | InlineCheck
  | PracticeTask
  | Quiz;

export interface QuickStart {
  minutes: 5;
  steps: { module: ModuleId; slide: string }[];
  check: Quiz;
}
export interface CheatSheet {
  title: Text;
  rows: { job: Text; where: Text; check: Text }[];
  /** "What the icons mean", the short legend of the draft table (round 2, 12 Oct 2026). */
  icons?: { title: Text; lines: Text[] };
}
export interface Challenge {
  title: Text;
  items: { id: string; text: Text }[];
}
export type LinkSpot =
  | "queue"
  | "queue-closed"
  | "back-for-you"
  | "case"
  | "customer"
  | "lines"
  | "reply"
  | "price-approval";
export interface TrainingTarget {
  module: ModuleId;
  slide?: string;
  text: Text;
}
// content/links.ts: TRAINING_LINKS: Record<LinkSpot, TrainingTarget>;
//                   HELP_TOPIC_TRAINING: Partial<Record<HelpTopicId, TrainingTarget>>
